import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaPlus, FaEdit, FaTrash, FaSync, FaSearch } from 'react-icons/fa';
import inventoryService from '../services/inventoryService';
import '../styles/UbicacionInventarioManagement.css';

const UbicacionInventarioManagement = () => {

    const [ubicaciones, setUbicaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUbicacion, setCurrentUbicacion] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 10;
    const [formData, setFormData] = useState({
        UBICACIONINVENTARIO_NOMBRE: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        loadUbicaciones(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const loadUbicaciones = async (page = 1, ps = 10) => {
        setLoading(true);
        try {
            const result = await inventoryService.locations.getLocations(page, ps, searchTerm);
            if (result.success) {
                const payload = result.data;
                if (Array.isArray(payload)) {
                    // Server returned full array (legacy). If caller requested pagination (ps provided),
                    // slice client-side so selector 'Por página' works even if backend didn't paginate.
                    const all = (payload || []).filter(u => u.UBICACIONINVENTARIO_ID !== 'LOC-000');
                    const tot = all.length;
                    if (ps && ps > 0) {
                        const start = (page - 1) * ps;
                        const pageRows = all.slice(start, start + ps);
                        setUbicaciones(pageRows);
                        setTotal(tot);
                        setTotalPages(Math.max(1, Math.ceil(tot / ps)));
                        setCurrentPage(page);
                        setPageSize(ps);
                    } else {
                        setUbicaciones(all);
                        setTotal(tot);
                        setTotalPages(1);
                        setCurrentPage(1);
                        setPageSize(ps);
                    }
                } else if (payload && payload.data) {
                    const data = (payload.data || []).filter(u => u.UBICACIONINVENTARIO_ID !== 'LOC-000');
                    setUbicaciones(data);
                    const tot = payload.total || 0;
                    const computedPages = payload.totalPages || Math.ceil(tot / ps);
                    setTotal(tot);
                    setTotalPages(Math.max(1, computedPages));
                    setCurrentPage(payload.page || page);
                    setPageSize(payload.pageSize || ps);
                } else {
                    setUbicaciones([]);
                    setTotal(0);
                    setTotalPages(1);
                }
            } else {
                setError(result.message || 'Error al cargar las ubicaciones');
            }
        } catch (err) {
            setError('Error al cargar las ubicaciones');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Server-side pagination: ubicaciones contains current page rows
    const paginatedUbicaciones = ubicaciones;
    const goToPage = (page) => setCurrentPage(page);

    // Resetear página cuando cambie la búsqueda
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    // Debounce search and reload
    useEffect(() => {
        const t = setTimeout(() => {
            setCurrentPage(1);
            loadUbicaciones(1, pageSize);
        }, 350);
        return () => clearTimeout(t);
    }, [searchTerm]);



    const validateForm = () => {
        const newErrors = {};
        if (!formData.UBICACIONINVENTARIO_NOMBRE.trim()) {
            newErrors.UBICACIONINVENTARIO_NOMBRE = 'El nombre de la ubicación es requerido';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const locationData = { UBICACIONINVENTARIO_NOMBRE: formData.UBICACIONINVENTARIO_NOMBRE };
            let result;
            if (currentUbicacion) {
                console.log('Actualizando ubicación:', currentUbicacion.UBICACIONINVENTARIO_ID, locationData);
                console.log('Objeto currentUbicacion completo:', currentUbicacion);
                result = await inventoryService.locations.updateLocation(currentUbicacion.UBICACIONINVENTARIO_ID, locationData);
            } else {
                console.log('Creando nueva ubicación:', locationData);
                result = await inventoryService.locations.createLocation(locationData);
            }

            console.log('Respuesta del servidor:', result);
            
            if (result.success) {
                alert(result.message);
                await loadUbicaciones();
                handleCloseModal();
            } else {
                alert('Error: ' + result.message);
            }
        } catch (err) {
            console.error('Error:', err);
            alert('Error al procesar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({ UBICACIONINVENTARIO_NOMBRE: '' });
        setCurrentUbicacion(null);
        setErrors({});
    };

    const handleCloseModal = () => {
        setShowForm(false);
        resetForm();
    };

    const handleEdit = (ubicacion) => {
        setCurrentUbicacion(ubicacion);
        setFormData({ UBICACIONINVENTARIO_NOMBRE: ubicacion.UBICACIONINVENTARIO_NOMBRE || '' });
        setShowForm(true);
        setErrors({});
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Eliminar ubicación? Los productos se reasignarán automáticamente.')) {
            setLoading(true);
            try {
                const result = await inventoryService.locations.deleteLocation(id);
                if (result.success) {
                    alert(result.message);
                    await loadUbicaciones();
                } else {
                    alert('Error: ' + result.message);
                }
            } catch (err) {
                console.error('Error:', err);
                alert('Error al eliminar');
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="ubicacion-inventario-management-container">
                <div className="ubicacion-inventario-management-header">
                    <h1>Gestión de Ubicaciones</h1>
                    <p>Cargando ubicaciones...</p>
                </div>
                <div style={{ textAlign: 'center', padding: '50px' }}>Cargando...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ubicacion-inventario-management-container">
                <div className="ubicacion-inventario-management-header">
                    <h1>Gestión de Ubicaciones</h1>
                    <p style={{ color: 'red' }}>Error: {error}</p>
                </div>
                <button onClick={loadUbicaciones}>Reintentar</button>
            </div>
        );
    }

    return (
        <div className="inventory-management-container">
            <div className="inventory-management-header">
                <h1>Gestión de Ubicaciones</h1>
                <p>Administre las ubicaciones del inventario médico de FEPAST</p>
            </div>
            <div className="inventory-management-actions">
                <div className="search-section">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Buscar ubicaciones por nombre o ID..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>

                <div className="action-buttons">
                    <button className="register-btn" onClick={() => window.location.href = '/inventory'}>
                        <FaMapMarkerAlt /> Ver Inventario
                    </button>
                    <button className="register-btn" onClick={loadUbicaciones} disabled={loading}>
                        <FaSync /> {loading ? 'Cargando...' : 'Actualizar'}
                    </button>
                    <button className="register-btn" onClick={() => setShowForm(true)}>
                        <FaPlus /> Nueva Ubicación
                    </button>
                </div>
            </div>
            <div className="inventory-table-container">
                <table className="inventory-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Ubicación</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedUbicaciones.length > 0 ? (
                            paginatedUbicaciones.map((ubicacion) => (
                                <tr key={ubicacion.UBICACIONINVENTARIO_ID}>
                                    <td><span className="product-id">{ubicacion.UBICACIONINVENTARIO_ID}</span></td>
                                    <td>
                                        <div className="product-name">
                                            <div className="product-icon">
                                                <FaMapMarkerAlt />
                                            </div>
                                            {ubicacion.UBICACIONINVENTARIO_NOMBRE}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="actions">
                                            <button
                                                className="action-btn edit-btn"
                                                onClick={() => {
                                                    if (ubicacion.UBICACIONINVENTARIO_ID === 'LOC-000') {
                                                        alert('No se puede editar la ubicación por defecto');
                                                        return;
                                                    }
                                                    handleEdit(ubicacion);
                                                }}
                                                title="Editar ubicación"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="action-btn delete-btn"
                                                onClick={() => {
                                                    if (ubicacion.UBICACIONINVENTARIO_ID === 'LOC-000') {
                                                        alert('No se puede eliminar la ubicación por defecto');
                                                        return;
                                                    }
                                                    if (window.confirm('¿Está seguro de que desea eliminar esta ubicación?')) {
                                                        handleDelete(ubicacion.UBICACIONINVENTARIO_ID);
                                                    }
                                                }}
                                                title="Eliminar ubicación"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                    </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>
                                    No hay ubicaciones registradas
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                    Mostrando <strong>{ubicaciones.length}</strong> de <strong>{total}</strong> ubicaciones
                    {searchTerm && ` (filtradas)`}
                </div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Anterior</button>
                    <span> Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong> </span>
                    <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Siguiente</button>
                    <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>Por página:</span>
                        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </select>
                    </label>
                </div>
            </div>
            
            {showForm && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <FaMapMarkerAlt />
                                {currentUbicacion ? 'Editar' : 'Nueva'} Ubicación
                            </h3>
                            <button className="modal-close" onClick={handleCloseModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit} className="ubicacion-inventario-form">
                                <div className="form-section">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="UBICACIONINVENTARIO_NOMBRE">Nombre de la Ubicación</label>
                                            <input
                                                type="text"
                                                id="UBICACIONINVENTARIO_NOMBRE"
                                                name="UBICACIONINVENTARIO_NOMBRE"
                                                value={formData.UBICACIONINVENTARIO_NOMBRE}
                                                onChange={handleInputChange}
                                                placeholder="Ej: Bodega Principal, Estante A1..."
                                                className={errors.UBICACIONINVENTARIO_NOMBRE ? 'error' : ''}
                                            />
                                            {errors.UBICACIONINVENTARIO_NOMBRE && <span className="error-message">{errors.UBICACIONINVENTARIO_NOMBRE}</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="form-actions">
                                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>Cancelar</button>
                                    <button type="submit" className="btn-primary" disabled={loading}>
                                        {loading ? 'Procesando...' : (currentUbicacion ? 'Actualizar' : 'Crear')} Ubicación
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UbicacionInventarioManagement;